import type { ConfidenceGainEntry } from '../types';

export const CHOICE_CONFIDENCE_SCENE_ID = '__CHOICE_CONFIDENCE_GAIN__';

export function chapterConfidenceStartKey(characterId: string): string {
  return `${characterId}_confidence_chapter_start`;
}

export function computeConfidenceTarget(currentLevel: number, amount: number): number {
  return Math.min(100, Math.max(0, currentLevel + amount));
}

export interface ResolvedChoiceConfidenceGain {
  characterId: string;
  amount: number;
  startLevel: number;
  targetLevel: number;
}

export function resolveChoiceConfidenceGains(
  gains: ConfidenceGainEntry[] | undefined,
  getConfidence: (characterId: string) => number,
  validCharacterIds: Set<string> | string[],
): ResolvedChoiceConfidenceGain[] {
  if (!gains?.length) return [];
  const validIds = validCharacterIds instanceof Set ? validCharacterIds : new Set(validCharacterIds);
  return gains
    .filter((g) => g.amount > 0 && validIds.has(g.characterId))
    .map((g) => {
      const startLevel = getConfidence(g.characterId);
      return {
        characterId: g.characterId,
        amount: g.amount,
        startLevel,
        targetLevel: computeConfidenceTarget(startLevel, g.amount),
      };
    })
    .filter((g) => g.targetLevel > g.startLevel);
}

export function snapshotChapterConfidence(
  variables: Record<string, number>,
  characterIds: string[],
): Record<string, number> {
  const next = { ...variables };
  for (const id of characterIds) {
    next[chapterConfidenceStartKey(id)] = variables[`${id}_confidence`] ?? 0;
  }
  return next;
}

export interface ChapterConfidenceGain {
  characterId: string;
  delta: number;
  current: number;
}

export function computeChapterConfidenceGains(
  variables: Record<string, number>,
  characterIds: string[],
): ChapterConfidenceGain[] {
  return characterIds
    .map((characterId) => {
      const current = variables[`${characterId}_confidence`] ?? 0;
      const start = variables[chapterConfidenceStartKey(characterId)] ?? 0;
      return { characterId, delta: current - start, current };
    })
    .filter((g) => g.delta > 0);
}
